import React, { useState, useEffect, ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Snackbar,
  Typography,
  Slide,
  SlideProps,
} from '@mui/material';
import {
  WifiOff as OfflineIcon,
  Wifi as OnlineIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface OfflineDetectorProps {
  children: ReactNode;
  showSnackbar?: boolean;
  showBanner?: boolean;
  onOnline?: () => void;
  onOffline?: () => void;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

export const OfflineDetector: React.FC<OfflineDetectorProps> = ({
  children,
  showSnackbar = true,
  showBanner = true,
  onOnline,
  onOffline,
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineSnackbar, setShowOfflineSnackbar] = useState(false);
  const [showOnlineSnackbar, setShowOnlineSnackbar] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline && showSnackbar) {
        setShowOnlineSnackbar(true);
        setWasOffline(false);
      }
      onOnline?.();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      if (showSnackbar) {
        setShowOfflineSnackbar(true);
      }
      onOffline?.();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline, showSnackbar, onOnline, onOffline]);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleCloseSnackbar = () => {
    setShowOfflineSnackbar(false);
    setShowOnlineSnackbar(false);
  };

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && showBanner && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1200 }}>
          <Alert 
            severity="warning" 
            icon={<OfflineIcon />}
            sx={{ 
              borderRadius: 0,
              '& .MuiAlert-message': { width: '100%' }
            }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle>You're offline</AlertTitle>
            <Typography variant="body2">
              Some features may not work properly. Check your internet connection and try again.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ opacity: isOnline ? 1 : 0.7, transition: 'opacity 0.3s ease' }}>
        {children}
      </Box>

      {/* Offline Snackbar */}
      <Snackbar
        open={showOfflineSnackbar}
        onClose={handleCloseSnackbar}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="warning" 
          icon={<OfflineIcon />}
          sx={{ width: '100%' }}
        >
          <AlertTitle>Connection Lost</AlertTitle>
          You're now offline. Some features may not work properly.
        </Alert>
      </Snackbar>

      {/* Online Snackbar */}
      <Snackbar
        open={showOnlineSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          icon={<OnlineIcon />}
          sx={{ width: '100%' }}
        >
          <AlertTitle>Connection Restored</AlertTitle>
          You're back online! The calendar will refresh automatically.
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfflineDetector;